import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import FavoriteIcon from '@mui/icons-material/Favorite'
import { Button, Card, FormControl, MenuItem, Select } from '@mui/material'
import type { ReactNode } from 'react'
import { deliveryReason, type SmsDeliveryState, type SmsDeliveryStatus } from '@/features/sms/smsService'
import type { ConfirmationStatus } from '../../registrationService'
import styles from './RegistrationCard.module.scss'

export const confirmationDetails: Readonly<Record<ConfirmationStatus, Readonly<{ label: string; className: string; icon: typeof CheckIcon }>>> = {
  confirmed: { label: 'Confirmed', className: styles.confirmed ?? '', icon: CheckIcon },
  maybe: { label: 'Maybe', className: styles.maybe ?? '', icon: FavoriteIcon },
  unreachable: { label: 'Unreachable', className: styles.unreachable ?? '', icon: CloseIcon },
}

export function ConfirmationLabel({ status }: { status: ConfirmationStatus }) {
  const details = confirmationDetails[status]
  const Icon = details.icon
  return <span className={`${styles.confirmationLabel} ${details.className}`}><Icon fontSize="small" aria-hidden="true" />{details.label}</span>
}

const shortTime = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' })

const deliveryDetails: Readonly<Record<SmsDeliveryState, Readonly<{ label: string; className: string }>>> = {
  sending: { label: 'Texting…', className: styles.smsQueued ?? '' },
  delivered: { label: 'Text delivered', className: styles.smsSent ?? '' },
  not_delivered: { label: 'Text not delivered', className: styles.smsFailed ?? '' },
  not_sent: { label: 'Text not sent', className: styles.smsFailed ?? '' },
  unconfirmed: { label: 'Delivery not confirmed', className: styles.smsQueued ?? '' },
}

/** Whether the person got their text, in one line, with the reason when there is one. */
export function DeliveryLine({ status }: { status: SmsDeliveryStatus }) {
  const details = deliveryDetails[status.state]
  const reason = status.state === 'not_delivered' || status.state === 'not_sent' ? deliveryReason(status.reason) : null
  const when = status.state === 'delivered' && status.at ? ` · ${shortTime.format(new Date(status.at))}` : ''
  return (
    <span className={`${styles.smsStatus} ${details.className}`}>
      {details.label}{when}
      {reason ? <span className={styles.smsReason}>{reason.charAt(0).toUpperCase() + reason.slice(1)}</span> : null}
    </span>
  )
}

type Action = Readonly<{ onClick: () => void; busy: boolean }>

export type RegistrationCardProps = Readonly<{
  name: string
  online: boolean
  /** The follow-up outcome and its picker. */
  outcome: Readonly<{ value: ConfirmationStatus; onChange: (next: ConfirmationStatus) => void; saving: boolean }>
  /** Phone, email or social handle, on one line. */
  methods?: string | null
  /** Where and when ("Saturday team · 24 Oct, 13:05", or who registered them). */
  meta: string
  /** Who last set the outcome ("Confirmed by Priya · 17:42"). */
  setBy?: string | null
  /** Left of the ticket row: Remove in All Mine, check-in for leads. */
  leading?: ReactNode
  /** Null when the event doesn't use tickets. */
  ticket: Readonly<{ reference: string | null; onView: () => void; issue: Action }> | null
  delivery: SmsDeliveryStatus | null
  /** A ticket and no phone number: say no text is coming. */
  noPhone?: boolean
  resend?: Action | null
  sendNow?: Action | null
}>

/**
 * One registration as a card, the same in All Mine and a session's
 * Registrations: name and follow-up, how to reach them, where and when, the
 * ticket, and whether its text arrived, with Resend or Send now where they help.
 */
export function RegistrationCard({ name, online, outcome, methods, meta, setBy, leading, ticket, delivery, noPhone, resend, sendNow }: RegistrationCardProps) {
  return (
    <Card className={styles.contact}>
      <span className={styles.contactHeader}>
        <span className={styles.name}>{name}</span>
        <FormControl className={styles.confirmationControl} size="small">
          <Select
            aria-label={`Confirmation status for ${name}`}
            disabled={!online || outcome.saving}
            renderValue={(value) => <ConfirmationLabel status={value} />}
            value={outcome.value}
            onChange={(event) => outcome.onChange(event.target.value)}
          >
            {(Object.keys(confirmationDetails) as ConfirmationStatus[]).map((status) => <MenuItem key={status} value={status}><ConfirmationLabel status={status} /></MenuItem>)}
          </Select>
        </FormControl>
      </span>
      {methods ? <span className={styles.methods}>{methods}</span> : null}
      <span className={styles.meta}>{meta}</span>
      {setBy ? <span className={styles.setBy}>{setBy}</span> : null}
      <span className={styles.ticketRow}>
        {leading ? <span className={styles.leading}>{leading}</span> : null}
        {ticket ? (
          ticket.reference ? (
            <Button size="small" variant="outlined" disabled={!online} aria-label={`View ticket for ${name}`} onClick={ticket.onView}>View ticket</Button>
          ) : (
            // Asking for a missing ticket issues it and texts the person, so it's never labelled View.
            <Button size="small" variant="outlined" disabled={!online || ticket.issue.busy} onClick={ticket.issue.onClick}>{ticket.issue.busy ? 'Issuing ticket…' : 'Issue ticket'}</Button>
          )
        ) : <span className={styles.notTicketed}>Not ticketed</span>}
      </span>
      {noPhone ? (
        <span className={styles.smsRow}>
          <span className={`${styles.smsStatus} ${styles.smsNone}`}>No text: no phone number<span className={styles.smsReason}>Use View ticket, then Share ticket</span></span>
        </span>
      ) : null}
      {delivery ? (
        <span className={styles.smsRow}>
          <DeliveryLine status={delivery} />
          {resend ? <Button size="small" disabled={!online || resend.busy} onClick={resend.onClick}>{resend.busy ? 'Sending…' : 'Resend'}</Button> : null}
          {sendNow ? <Button size="small" disabled={!online || sendNow.busy} onClick={sendNow.onClick}>{sendNow.busy ? 'Sending…' : 'Send now'}</Button> : null}
        </span>
      ) : null}
    </Card>
  )
}
